import { useContext } from 'react';
import {
  ErrorScreenProps,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { PageDownErrorMessageProps } from 'shared/patterns/vxl/errors/PageDownErrorMessage/PageDownErrorMessage';
import { AuthContext } from 'shared/providers/auth';
import { buildNumber, displayVersion } from 'shared/utils/appVersionUtils';
import { ClassifiedErrorType } from 'shared/utils/errors/classifyError';
import { getErrorDisplayInfo } from 'shared/utils/errors/errorDisplayUtils';
import { Maybe } from 'shared/utils/types';

import { useErrorDisplayStrings } from './useErrorDisplayStrings';

export const ErrorDisplayType = {
  Inline: 'inline',
  Page: 'page',
  Tab: 'tab',
} as const;

export type ErrorDisplayType =
  (typeof ErrorDisplayType)[keyof typeof ErrorDisplayType];

export const ErrorComponentType = {
  ErrorScreen: 'ErrorScreen',
  PageDownErrorMessage: 'PageDownErrorMessage',
} as const;

export type ErrorComponentType =
  (typeof ErrorComponentType)[keyof typeof ErrorComponentType];

export interface ErrorDisplayButton {
  /** Defaults to the localized "Try again" for the primary button */
  title?: string;
  onPress: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export interface UseErrorDisplayOptions {
  /**
   * Visual treatment:
   * - 'inline': full-page error within a feature flow (ErrorScreen)
   * - 'page': service downtime with illustration (PageDownErrorMessage)
   * - 'tab': tab-level error with try again + log out (PageDownErrorMessage)
   */
  displayType?: ErrorDisplayType;
  /** Override the classified error title */
  messageHeadline?: string;
  /** Override the classified error message */
  messageBody?: string;
  primaryButton?: ErrorDisplayButton;
  secondaryButton?: ErrorDisplayButton;
  hasPageHeader?: boolean;
}

export type ErrorDisplayResult =
  | {
      componentType: typeof ErrorComponentType.ErrorScreen;
      errorScreenProps: ErrorScreenProps;
      classifiedType: ClassifiedErrorType;
    }
  | {
      componentType: typeof ErrorComponentType.PageDownErrorMessage;
      pageDownProps: PageDownErrorMessageProps;
      classifiedType: ClassifiedErrorType;
    };

/**
 * Derives fully-populated props for the right error component from any
 * error. Copy is selected by error classification and localized; overrides
 * win when provided.
 */
export const useErrorDisplay = (
  error: Maybe<unknown>,
  options: UseErrorDisplayOptions = {},
): Maybe<ErrorDisplayResult> => {
  const { handleLogout } = useContext(AuthContext);
  const strings = useErrorDisplayStrings();

  if (!error) {
    return null;
  }

  const displayType = options.displayType ?? ErrorDisplayType.Inline;
  const displayInfo = getErrorDisplayInfo(error, strings);

  // Intentionally cancelled requests should not render error UI
  if (displayInfo.type === 'abort') {
    return null;
  }
  const title = options.messageHeadline ?? displayInfo.title;
  const message = options.messageBody ?? displayInfo.message;

  if (displayType === ErrorDisplayType.Inline) {
    return {
      componentType: ErrorComponentType.ErrorScreen,
      classifiedType: displayInfo.type,
      errorScreenProps: {
        screenType: ErrorScreenType.Error,
        messageHeadline: title,
        messageBody: message,
        primaryButton: options.primaryButton
          ? {
              ...options.primaryButton,
              title: options.primaryButton.title ?? strings.tryAgainButtonTitle,
            }
          : undefined,
        secondaryButton:
          options.secondaryButton && options.secondaryButton.title
            ? {
                ...options.secondaryButton,
                title: options.secondaryButton.title,
              }
            : undefined,
        error,
      },
    };
  }

  const primaryButton = options.primaryButton ?? {
    title: strings.tryAgainButtonTitle,
    onPress: () => {},
  };

  const tabSecondaryButton: ErrorDisplayButton = {
    title: strings.logOutButtonTitle,
    onPress: () => void handleLogout(),
  };
  const secondaryButton =
    displayType === ErrorDisplayType.Tab
      ? (options.secondaryButton ?? tabSecondaryButton)
      : options.secondaryButton;

  return {
    componentType: ErrorComponentType.PageDownErrorMessage,
    classifiedType: displayInfo.type,
    pageDownProps: {
      error,
      illustrationKey: 'HOURGLASS_A',
      title,
      message,
      primaryButtonText: primaryButton.title ?? strings.tryAgainButtonTitle,
      primaryOnPress: primaryButton.onPress,
      primaryButtonIsLoading: primaryButton.isLoading,
      primaryButtonIsDisabled: primaryButton.isDisabled,
      secondaryButtonText: secondaryButton?.title,
      secondaryOnPress: secondaryButton?.onPress,
      footerText:
        displayType === ErrorDisplayType.Tab
          ? `App version ${displayVersion} ${buildNumber ? `(${buildNumber})` : ''}`
          : undefined,
      hasPageHeader: options.hasPageHeader,
    },
  };
};
